import Layout from "@/components/layout/Layout";
import { JSX, useEffect, useState } from "react";
import CreateAdCategoriesStepOne from "./component/step-one";
import Button from "@/components/ui/Button";
import CreateAdCategoriesStepTwo from "./component/step-two";
import CreateAdCategoriesStepThree from "./component/step-three";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import CreateAdCategoriesStepFour from "./component/step-four";
import CreateAdCategoriesStepSix from "./component/step-six";
import { AdCategoryValidation } from "./validation";
import useGlobalStore from "@/store/globalStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { convertToAPIFormat, updateDataConvertToAPIFormat } from "./helper";
import CreateAdCategoriesStepFive from "./component/step-five";
import { SaveIcon } from "lucide-react";

export default function CreateAdCategories(): JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const {
    generateAdTypes,
    saveAdCategoryData,
    getAdCategoryDetailData,
    setEditData,
    updateAdCategory,
    saveCanvasBuilderData,
  } = useAdCategoryStore(
    useShallow((state) => ({
      generateAdTypes: state.generateAdTypes,
      saveAdCategoryData: state.saveAdCategoryData,
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      setEditData: state.setEditData,
      updateAdCategory: state.updateAdCategory,
      saveCanvasBuilderData: state.saveCanvasBuilderData,
    })),
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const state = useAdCategoryStore();

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    })),
  );

  const isEditMode = !!searchParams.get("id");

  const fetchUpdateData = async () => {
    await getAdCategoryDetailData(searchParams.get("id"));
    const detailData = useAdCategoryStore.getState().adCategoryDetailData;
    if (detailData) {
      setEditData(detailData);
    }
  };

  useEffect(() => {
    console.log(state);
  }, [state]);

  useEffect(() => {
    const id = searchParams.get("id");
    const step = searchParams.get("step");

    // Only fetch data on initial load or when ID changes
    // Don't refetch when just navigating between steps or view modes
    if (id && !useAdCategoryStore.getState().editingAdCategoryId) {
      fetchUpdateData();
    }

    if (step) {
      setCurrentStep(Number(step) - 1);
    }
  }, [searchParams]);

  const isViewMode = searchParams.get("viewAdType") === "true";

  // Save the current step in edit mode and navigate back
  const handleSave = async () => {
    const id = searchParams.get("id");

    const validation = AdCategoryValidation.validateStep(currentStep, state);
    if (!validation.isValid) {
      AdCategoryValidation.formatErrors(validation.errors).forEach((e) =>
        setError(e),
      );
      return;
    }

    try {
      if (currentStep === 0 && id) {
        await updateAdCategory("1", id, { name: state.adCategory });
      } else if (currentStep === 1 && id) {
        const output = state.publishers.map((publisher) => ({
          ...(publisher.id && { id: publisher.id }),
          name: publisher.name ?? "N/A",
          type:
            state.publisherType === "single_publisher"
              ? "single"
              : state.publisherType === "multiple_publisher"
                ? "multi"
                : state.publisherType,
        }));
        await updateAdCategory("2", id, { publishers: output });
        generateAdTypes();
      } else if (currentStep === 2 && id) {
        const ad_types = state.adTypes.flatMap((adType) =>
          adType.adInputs.map((adInput) => ({
            ...(adInput.id && { id: adInput.id }),
            name: adInput.name,
            media_type: adInput.mediaType,
            ...(adType.publisherId && { publisher_id: adType.publisherId }),
          })),
        );
        await updateAdCategory("3", id, { ad_types });
      } else if (currentStep === 3 && id) {
        const requestData = updateDataConvertToAPIFormat(state);
        await updateAdCategory("4", id, requestData);
      } else if (currentStep === 4) {
        const canvasState = useAdCategoryStore.getState();
        const adTypeId = searchParams.get("adInputId") || canvasState.canvasBuilderData?.ad_type?.id;
        if (adTypeId) {
          const payload: Record<string, any> = {
            interface_id: canvasState.selectedInterface || undefined,
            ad_spec_overview: canvasState.selectedAdSpecs,
            ad_info_blocks_overview: canvasState.selectedAdInfoBlocks,
          };
          await saveCanvasBuilderData(adTypeId, payload);
        }
      } else if (currentStep === 5) {
        if (!id) {
          const requestData = convertToAPIFormat(state);
          await saveAdCategoryData(requestData);
          showSuccess("Ad Category Created Successfully");
        }
      }

      showSuccess("Saved successfully");
      navigate("/ad-categories");
    } catch (error: any) {
      setError(error.message ?? "Failed to save");
    }
  };

  // Navigate to the next step (create flow only)
  const goToStep = (step: number) => {
    setCurrentStep(step);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", (step + 1).toString());
    setSearchParams(newParams);
  };

  return (
    <Layout>
      <div>
        {currentStep !== 4 && currentStep !== 5 && (
          <div className="text-black font-bold text-2xl">
            {isEditMode ? "Edit Ad Category" : "Create Ad Category"}
          </div>
        )}

        {currentStep === 0 && <CreateAdCategoriesStepOne />}
        {currentStep === 1 && <CreateAdCategoriesStepTwo />}
        {currentStep === 2 && <CreateAdCategoriesStepThree />}
        {currentStep === 3 && <CreateAdCategoriesStepFour />}
        {currentStep === 4 && <CreateAdCategoriesStepFive />}
        {currentStep === 5 && <CreateAdCategoriesStepSix />}

        {!isViewMode && <hr className="mt-10 border-1 border-gray-300 mb-2" />}
        {!isViewMode && (
          <div className="flex">
            {/* Previous: only in create flow, hidden in edit mode */}
            {!isEditMode && currentStep !== 0 && (
              <Button
                type="primary"
                text="Previous"
                className="mt-4 px-9 mr-3"
                onClick={() => {
                  // In create flow, Review (step 5) goes back to Ad Type (step 2),
                  // skipping the Parameters step (step 3) in the back direction too.
                  const prevStep = currentStep === 5 ? 2 : currentStep - 1;
                  goToStep(prevStep);
                }}
              />
            )}

            {isEditMode ? (
              /* Edit mode: single Save button */
              <Button type="primary" className="mt-4 px-9" onClick={handleSave}>
                {currentStep === 5 ? "Approve" : "Save"}
              </Button>
            ) : (
              <>
                {/* Create mode: Save Progress + Next */}
                <Button
                  type="secondary"
                  text="Save Progress"
                  className="mt-4 mr-3"
                  leadingIcon={<SaveIcon size={18} />}
                  onClick={async () => {
                    const id = searchParams.get("id");
                    if (!id) {
                      setError("Cannot save progress: No ID found");
                      return;
                    }

                    try {
                      if (currentStep === 0) {
                        await updateAdCategory("1", id, {
                          name: state.adCategory,
                        });
                        showSuccess("Step 1 saved successfully");
                      } else if (currentStep === 1) {
                        const output = state.publishers.map((publisher) => ({
                          ...(publisher.id && { id: publisher.id }),
                          name: publisher.name ?? "N/A",
                          type:
                            state.publisherType === "single_publisher"
                              ? "single"
                              : state.publisherType === "multiple_publisher"
                                ? "multi"
                                : state.publisherType,
                        }));
                        await updateAdCategory("2", id, { publishers: output });
                        showSuccess("Step 2 saved successfully");
                      } else if (currentStep === 2) {
                        const ad_types = state.adTypes.flatMap((adType) =>
                          adType.adInputs.map((adInput) => ({
                            ...(adInput.id && { id: adInput.id }),
                            name: adInput.name,
                            media_type: adInput.mediaType,
                            ...(adType.publisherId && {
                              publisher_id: adType.publisherId,
                            }),
                          })),
                        );
                        await updateAdCategory("3", id, { ad_types });
                        showSuccess("Step 3 saved successfully");
                      } else if (currentStep === 3) {
                        const requestData = updateDataConvertToAPIFormat(state);
                        await updateAdCategory("4", id, requestData);
                        showSuccess("Step 4 saved successfully");
                      } else if (currentStep === 4) {
                        const canvasState = useAdCategoryStore.getState();
                        const adTypeId = searchParams.get("adInputId") || canvasState.canvasBuilderData?.ad_type?.id;
                        if (adTypeId) {
                          const payload: Record<string, any> = {
                            interface_id: canvasState.selectedInterface || undefined,
                            ad_spec_overview: canvasState.selectedAdSpecs,
                            ad_info_blocks_overview: canvasState.selectedAdInfoBlocks,
                          };
                          await saveCanvasBuilderData(adTypeId, payload);
                        }
                        showSuccess("Step 5 saved successfully");
                      } else if (currentStep === 5) {
                        showSuccess("Step 6 saved successfully");
                      }

                      navigate("/ad-categories");
                    } catch (error: any) {
                      setError(error.message ?? "Failed to save progress");
                    }
                  }}
                />

                <Button
                  type="primary"
                  className="mt-4 px-9"
                  onClick={async () => {
                    const validation = AdCategoryValidation.validateStep(
                      currentStep,
                      state,
                    );

                    if (!validation.isValid) {
                      AdCategoryValidation.formatErrors(
                        validation.errors,
                      ).forEach((e) => setError(e));
                      return;
                    }

                    const id = searchParams.get("id");

                    try {
                      if (currentStep === 0 && id) {
                        await updateAdCategory("1", id, {
                          name: state.adCategory,
                        });
                      }

                      if (currentStep === 1) {
                        if (id) {
                          const output = state.publishers.map((publisher) => ({
                            ...(publisher.id && { id: publisher.id }),
                            name: publisher.name ?? "N/A",
                            type:
                              state.publisherType === "single_publisher"
                                ? "single"
                                : state.publisherType === "multiple_publisher"
                                  ? "multi"
                                  : state.publisherType,
                          }));
                          await updateAdCategory("2", id, {
                            publishers: output,
                          });
                        }
                        generateAdTypes();
                      }

                      if (currentStep === 2) {
                        if (id) {
                          const ad_types = state.adTypes.flatMap((adType) =>
                            adType.adInputs.map((adInput) => ({
                              ...(adInput.id && { id: adInput.id }),
                              name: adInput.name,
                              media_type: adInput.mediaType,
                              ...(adType.publisherId && {
                                publisher_id: adType.publisherId,
                              }),
                            })),
                          );
                          await updateAdCategory("3", id, { ad_types });
                        }

                        goToStep(5);
                        return;
                      }

                      if (currentStep === 3 && id) {
                        const requestData = updateDataConvertToAPIFormat(state);
                        await updateAdCategory("4", id, requestData);
                      }

                      if (currentStep === 4) {
                        const canvasState = useAdCategoryStore.getState();
                        const adTypeId = searchParams.get("adInputId") || canvasState.canvasBuilderData?.ad_type?.id;
                        if (adTypeId) {
                          const payload: Record<string, any> = {
                            interface_id: canvasState.selectedInterface || undefined,
                            ad_spec_overview: canvasState.selectedAdSpecs,
                            ad_info_blocks_overview: canvasState.selectedAdInfoBlocks,
                          };
                          await saveCanvasBuilderData(adTypeId, payload);
                        }
                      }

                      // Step 5 (Review/Approve): final submit
                      if (currentStep === 5) {
                        if (id) {
                          navigate("/ad-categories");
                        } else {
                          const requestData = convertToAPIFormat(state);
                          await saveAdCategoryData(requestData);
                          showSuccess("Ad Category Created Successfully");
                          navigate("/ad-categories");
                        }
                        return;
                      }

                      // In create flow, skip step 4 (canvas template) — jump from step 3 to step 5
                      const nextStep = currentStep === 3 ? 5 : currentStep + 1;
                      goToStep(nextStep);
                    } catch (error: any) {
                      setError(error.message ?? "Something went wrong");
                    }
                  }}
                >
                  {currentStep === 5 ? "Approve" : "Next"}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
